/**
 * useOrders Hook
 * 
 * Custom React hook for managing orders in the Manager portal.
 * Provides state management, order operations, and error handling.
 * 
 * @module useOrders
 */

import { useState, useCallback, useRef } from 'react';
import type { QueryDocumentSnapshot } from 'firebase/firestore';
import {
  OrderManagementService,
  type AllOrdersQueryPlan
} from '../services/OrderManagementService';
import {
  Order,
  OrderFilters,
  OrderSort,
  OrderStatistics,
  OrderStatus,
  OrderActionResult,
  BulkOrderAction
} from '../@types/order';

/**
 * Hook state interface
 */
interface UseOrdersState {
  // Data
  orders: Order[];
  currentOrder: Order | null;
  statistics: OrderStatistics | null;
  
  // Pagination
  hasMore: boolean;
  /** True when Firestore returned a cursor so "Load more" can fetch the next page */
  canLoadMore: boolean;
  
  // Loading states
  isLoading: boolean;
  isLoadingMore: boolean;
  isLoadingStatistics: boolean;
  isPerformingAction: boolean;
  
  // Error state
  error: string | null;
}

/**
 * Hook return type
 */
interface UseOrdersReturn extends UseOrdersState {
  // Fetch operations
  fetchAllOrders: (filters?: OrderFilters, sort?: OrderSort, pageSize?: number) => Promise<void>;
  loadMoreOrders: () => Promise<void>;
  fetchOrdersByStore: (storeId: string, filters?: OrderFilters) => Promise<void>;
  fetchOrderById: (storeId: string, orderId: string) => Promise<void>;
  fetchStatistics: (dateRange?: { from: Date; to: Date }) => Promise<void>;
  
  // Status operations
  updateOrderStatus: (
    storeId: string,
    orderId: string,
    newStatus: OrderStatus,
    userId: string,
    notes?: string
  ) => Promise<OrderActionResult>;
  
  // Approval operations
  approveOrder: (storeId: string, orderId: string, userId: string, notes?: string, managerDisplayName?: string) => Promise<OrderActionResult>;
  rejectOrder: (storeId: string, orderId: string, userId: string, reason: string) => Promise<OrderActionResult>;
  
  // Flag operations
  flagOrder: (storeId: string, orderId: string, userId: string, reason: string) => Promise<OrderActionResult>;
  unflagOrder: (storeId: string, orderId: string, userId: string) => Promise<OrderActionResult>;
  
  // Bulk operations
  performBulkAction: (bulkAction: BulkOrderAction, userId: string) => Promise<OrderActionResult[]>;
  
  // Utility operations
  refreshOrders: () => Promise<void>;
  clearError: () => void;
  setCurrentOrder: (order: Order | null) => void;
}

/**
 * Custom hook for order management
 * 
 * @returns Order management functions and state
 * 
 * @example
 * ```tsx
 * function OrderManagement() {
 *   const {
 *     orders,
 *     isLoading,
 *     error,
 *     fetchAllOrders,
 *     approveOrder
 *   } = useOrders();
 * 
 *   useEffect(() => {
 *     fetchAllOrders({ status: 'submitted' });
 *   }, []);
 * 
 *   // ... render component
 * }
 * ```
 */
export function useOrders(): UseOrdersReturn {
  // State management
  const [state, setState] = useState<UseOrdersState>({
    orders: [],
    currentOrder: null,
    statistics: null,
    hasMore: false,
    canLoadMore: false,
    isLoading: false,
    isLoadingMore: false,
    isLoadingStatistics: false,
    isPerformingAction: false,
    error: null
  });

  /** Latest state for synchronous guards (React 18 may run setState updaters after the call returns) */
  const ordersStateRef = useRef(state);
  ordersStateRef.current = state;

  // Store current filters and sort for refresh
  const [currentFilters, setCurrentFilters] = useState<OrderFilters | undefined>();
  const [currentSort, setCurrentSort] = useState<OrderSort | undefined>();
  const [currentStoreId, setCurrentStoreId] = useState<string | undefined>();
  const [pageSize, setPageSize] = useState<number>(50);

  const ordersLastDocRef = useRef<QueryDocumentSnapshot | null>(null);
  const ordersQueryPlanRef = useRef<AllOrdersQueryPlan | null>(null);
  const loadMoreInFlightRef = useRef(false);

  /**
   * Fetch all orders with filters and sorting
   */
  const fetchAllOrders = useCallback(async (
    filters?: OrderFilters,
    sort?: OrderSort,
    size: number = 50
  ) => {
    try {
      ordersLastDocRef.current = null;
      ordersQueryPlanRef.current = null;
      loadMoreInFlightRef.current = false;
      setState(prev => ({
        ...prev,
        isLoading: true,
        isLoadingMore: false,
        error: null,
        canLoadMore: false
      }));
      
      // Enforce maximum page size for performance
      const maxPageSize = 50;
      const pageSize = Math.min(size, maxPageSize);
      
      // Store current parameters for refresh
      setCurrentFilters(filters);
      setCurrentSort(sort);
      setCurrentStoreId(undefined); // Clear store-specific filter
      setPageSize(pageSize);

      // Use filters as provided (no default status filter for manager account)
      const result = await OrderManagementService.getAllOrders(filters, sort, pageSize);

      ordersLastDocRef.current = result.lastSnapshot ?? null;
      ordersQueryPlanRef.current = result.queryPlan ?? null;
      const canLoadMore =
        result.hasMore &&
        !!result.lastSnapshot &&
        result.queryPlan?.kind === 'indexed';

      setState(prev => ({
        ...prev,
        orders: result.orders,
        hasMore: result.hasMore,
        canLoadMore,
        isLoading: false
      }));
    } catch (error: any) {
      console.error('Error fetching orders:', error);
      ordersLastDocRef.current = null;
      ordersQueryPlanRef.current = null;
      setState(prev => ({
        ...prev,
        isLoading: false,
        canLoadMore: false,
        error: error.message || 'Failed to fetch orders'
      }));
    }
  }, []);

  /**
   * Append the next page of orders (same filters/sort as the current list)
   */
  const loadMoreOrders = useCallback(async () => {
    const last = ordersLastDocRef.current;
    const plan = ordersQueryPlanRef.current;
    if (!last || !plan || plan.kind !== 'indexed') {
      return;
    }

    const s = ordersStateRef.current;
    if (!s.canLoadMore || s.isLoading || s.isLoadingMore || loadMoreInFlightRef.current) {
      return;
    }

    loadMoreInFlightRef.current = true;
    setState(prev => ({ ...prev, isLoadingMore: true, error: null }));

    try {
      const result = await OrderManagementService.getAllOrders(
        currentFilters,
        currentSort,
        pageSize,
        { startAfterSnapshot: last, queryPlan: plan }
      );

      ordersLastDocRef.current = result.lastSnapshot ?? null;
      ordersQueryPlanRef.current = result.queryPlan ?? null;
      const canLoadMore =
        result.hasMore &&
        !!result.lastSnapshot &&
        result.queryPlan?.kind === 'indexed';

      setState(prev => {
        const seen = new Set(prev.orders.map(o => `${o.storeId}|${o.orderId}`));
        const merged = [...prev.orders];
        for (const o of result.orders) {
          const key = `${o.storeId}|${o.orderId}`;
          if (!seen.has(key)) {
            seen.add(key);
            merged.push(o);
          }
        }
        return {
          ...prev,
          orders: merged,
          hasMore: result.hasMore,
          canLoadMore,
          isLoadingMore: false
        };
      });
    } catch (error: any) {
      console.error('Error loading more orders:', error);
      setState(prev => ({
        ...prev,
        isLoadingMore: false,
        error: error.message || 'Failed to load more orders'
      }));
    } finally {
      loadMoreInFlightRef.current = false;
    }
  }, [currentFilters, currentSort, pageSize]);

  /**
   * Fetch orders for a specific store
   */
  const fetchOrdersByStore = useCallback(async (
    storeId: string,
    filters?: OrderFilters
  ) => {
    try {
      setState(prev => ({ ...prev, isLoading: true, error: null }));
      
      // Store current parameters for refresh
      setCurrentStoreId(storeId);
      setCurrentFilters(filters);
      setCurrentSort(undefined); // Store-specific queries use default sorting

      const orders = await OrderManagementService.getOrdersByStore(storeId, filters);

      ordersLastDocRef.current = null;
      ordersQueryPlanRef.current = null;
      setState(prev => ({
        ...prev,
        orders,
        hasMore: false, // Store-specific queries don't support pagination yet
        canLoadMore: false,
        isLoadingMore: false,
        isLoading: false
      }));
    } catch (error: any) {
      console.error('Error fetching store orders:', error);
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: error.message || 'Failed to fetch store orders'
      }));
    }
  }, []);

  /**
   * Fetch a single order by ID
   */
  const fetchOrderById = useCallback(async (
    storeId: string,
    orderId: string
  ) => {
    try {
      setState(prev => ({ ...prev, isLoading: true, error: null }));

      const order = await OrderManagementService.getOrderById(storeId, orderId);

      setState(prev => ({
        ...prev,
        currentOrder: order,
        isLoading: false
      }));
    } catch (error: any) {
      console.error('Error fetching order:', error);
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: error.message || 'Failed to fetch order'
      }));
    }
  }, []);

  /**
   * Fetch order statistics
   */
  const fetchStatistics = useCallback(async (
    dateRange?: { from: Date; to: Date }
  ) => {
    try {
      setState(prev => ({ ...prev, isLoadingStatistics: true, error: null }));

      const stats = await OrderManagementService.getOrderStatistics(dateRange);

      setState(prev => ({
        ...prev,
        statistics: stats,
        isLoadingStatistics: false
      }));
    } catch (error: any) {
      console.error('Error fetching statistics:', error);
      setState(prev => ({
        ...prev,
        isLoadingStatistics: false,
        error: error.message || 'Failed to fetch statistics'
      }));
    }
  }, []);

  /**
   * Update order status
   */
  const updateOrderStatus = useCallback(async (
    storeId: string,
    orderId: string,
    newStatus: OrderStatus,
    userId: string,
    notes?: string
  ): Promise<OrderActionResult> => {
    try {
      setState(prev => ({ ...prev, isPerformingAction: true, error: null }));

      const result = await OrderManagementService.updateOrderStatus(
        storeId,
        orderId,
        newStatus,
        userId,
        notes
      );

      if (result.success) {
        // Update local state
        setState(prev => ({
          ...prev,
          orders: prev.orders.map(order =>
            order.orderId === orderId
              ? { ...order, status: newStatus, managerNotes: notes }
              : order
          ),
          currentOrder: prev.currentOrder?.orderId === orderId
            ? { ...prev.currentOrder, status: newStatus, managerNotes: notes }
            : prev.currentOrder
        }));
      }

      setState(prev => ({ ...prev, isPerformingAction: false }));
      return result;
    } catch (error: any) {
      console.error('Error updating order status:', error);
      setState(prev => ({
        ...prev,
        isPerformingAction: false,
        error: error.message || 'Failed to update order status'
      }));
      return {
        success: false,
        error: error.message || 'Failed to update order status'
      };
    }
  }, []);

  /**
   * Approve an order
   */
  const approveOrder = useCallback(async (
    storeId: string,
    orderId: string,
    userId: string,
    notes?: string,
    managerDisplayName?: string
  ): Promise<OrderActionResult> => {
    try {
      setState(prev => ({ ...prev, isPerformingAction: true, error: null }));

      const result = await OrderManagementService.approveOrder(
        storeId,
        orderId,
        userId,
        notes,
        managerDisplayName
      );

      if (result.success) {
        // Update local state
        setState(prev => ({
          ...prev,
          orders: prev.orders.map(order =>
            order.orderId === orderId
              ? {
                  ...order,
                  status: 'processing',
                  requiresApproval: false,
                  managerNotes: notes
                }
              : order
          ),
          currentOrder: prev.currentOrder?.orderId === orderId
            ? {
                ...prev.currentOrder,
                status: 'processing',
                requiresApproval: false,
                managerNotes: notes
              }
            : prev.currentOrder
        }));
      }

      setState(prev => ({ ...prev, isPerformingAction: false }));
      return result;
    } catch (error: any) {
      console.error('Error approving order:', error);
      setState(prev => ({
        ...prev,
        isPerformingAction: false,
        error: error.message || 'Failed to approve order'
      }));
      return {
        success: false,
        error: error.message || 'Failed to approve order'
      };
    }
  }, []);

  /**
   * Reject an order
   */
  const rejectOrder = useCallback(async (
    storeId: string,
    orderId: string,
    userId: string,
    reason: string
  ): Promise<OrderActionResult> => {
    try {
      setState(prev => ({ ...prev, isPerformingAction: true, error: null }));

      const result = await OrderManagementService.rejectOrder(
        storeId,
        orderId,
        userId,
        reason
      );

      if (result.success) {
        // Update local state
        setState(prev => ({
          ...prev,
          orders: prev.orders.map(order =>
            order.orderId === orderId
              ? {
                  ...order,
                  status: 'cancelled',
                  requiresApproval: false,
                  managerNotes: `Rejected: ${reason}`
                }
              : order
          ),
          currentOrder: prev.currentOrder?.orderId === orderId
            ? {
                ...prev.currentOrder,
                status: 'cancelled',
                requiresApproval: false,
                managerNotes: `Rejected: ${reason}`
              }
            : prev.currentOrder
        }));
      }

      setState(prev => ({ ...prev, isPerformingAction: false }));
      return result;
    } catch (error: any) {
      console.error('Error rejecting order:', error);
      setState(prev => ({
        ...prev,
        isPerformingAction: false,
        error: error.message || 'Failed to reject order'
      }));
      return {
        success: false,
        error: error.message || 'Failed to reject order'
      };
    }
  }, []);

  /**
   * Flag an order for review
   */
  const flagOrder = useCallback(async (
    storeId: string,
    orderId: string,
    userId: string,
    reason: string
  ): Promise<OrderActionResult> => {
    try {
      setState(prev => ({ ...prev, isPerformingAction: true, error: null }));

      const result = await OrderManagementService.flagOrder(
        storeId,
        orderId,
        userId,
        reason
      );

      if (result.success) {
        // Update local state
        setState(prev => ({
          ...prev,
          orders: prev.orders.map(order =>
            order.orderId === orderId
              ? { ...order, flaggedForReview: true, flagReason: reason }
              : order
          ),
          currentOrder: prev.currentOrder?.orderId === orderId
            ? { ...prev.currentOrder, flaggedForReview: true, flagReason: reason }
            : prev.currentOrder
        }));
      }

      setState(prev => ({ ...prev, isPerformingAction: false }));
      return result;
    } catch (error: any) {
      console.error('Error flagging order:', error);
      setState(prev => ({
        ...prev,
        isPerformingAction: false,
        error: error.message || 'Failed to flag order'
      }));
      return {
        success: false,
        error: error.message || 'Failed to flag order'
      };
    }
  }, []);

  /**
   * Unflag an order
   */
  const unflagOrder = useCallback(async (
    storeId: string,
    orderId: string,
    userId: string
  ): Promise<OrderActionResult> => {
    try {
      setState(prev => ({ ...prev, isPerformingAction: true, error: null }));

      const result = await OrderManagementService.unflagOrder(
        storeId,
        orderId,
        userId
      );

      if (result.success) {
        // Update local state
        setState(prev => ({
          ...prev,
          orders: prev.orders.map(order =>
            order.orderId === orderId
              ? { ...order, flaggedForReview: false, flagReason: '' }
              : order
          ),
          currentOrder: prev.currentOrder?.orderId === orderId
            ? { ...prev.currentOrder, flaggedForReview: false, flagReason: '' }
            : prev.currentOrder
        }));
      }

      setState(prev => ({ ...prev, isPerformingAction: false }));
      return result;
    } catch (error: any) {
      console.error('Error unflagging order:', error);
      setState(prev => ({
        ...prev,
        isPerformingAction: false,
        error: error.message || 'Failed to unflag order'
      }));
      return {
        success: false,
        error: error.message || 'Failed to unflag order'
      };
    }
  }, []);

  /**
   * Perform bulk action on multiple orders
   */
  const performBulkAction = useCallback(async (
    bulkAction: BulkOrderAction,
    userId: string
  ): Promise<OrderActionResult[]> => {
    try {
      setState(prev => ({ ...prev, isPerformingAction: true, error: null }));

      const results = await OrderManagementService.performBulkAction(
        bulkAction,
        userId
      );

      // Refresh orders after bulk action
      await refreshOrders();

      setState(prev => ({ ...prev, isPerformingAction: false }));
      return results;
    } catch (error: any) {
      console.error('Error performing bulk action:', error);
      setState(prev => ({
        ...prev,
        isPerformingAction: false,
        error: error.message || 'Failed to perform bulk action'
      }));
      return [];
    }
  }, []);

  /**
   * Refresh orders list
   */
  const refreshOrders = useCallback(async () => {
    if (currentStoreId) {
      await fetchOrdersByStore(currentStoreId, currentFilters);
    } else {
      await fetchAllOrders(currentFilters, currentSort, pageSize);
    }
  }, [
    currentStoreId,
    currentFilters,
    currentSort,
    pageSize,
    fetchOrdersByStore,
    fetchAllOrders
  ]);

  /**
   * Clear error message
   */
  const clearError = useCallback(() => {
    setState(prev => ({ ...prev, error: null }));
  }, []);

  /**
   * Set current order
   */
  const setCurrentOrder = useCallback((order: Order | null) => {
    setState(prev => ({ ...prev, currentOrder: order }));
  }, []);

  return {
    // State
    ...state,
    
    // Fetch operations
    fetchAllOrders,
    loadMoreOrders,
    fetchOrdersByStore,
    fetchOrderById,
    fetchStatistics,
    
    // Status operations
    updateOrderStatus,
    
    // Approval operations
    approveOrder,
    rejectOrder,
    
    // Flag operations
    flagOrder,
    unflagOrder,
    
    // Bulk operations
    performBulkAction,
    
    // Utility operations
    refreshOrders,
    clearError,
    setCurrentOrder
  };
}

